/**
 * Which period-end moments this device has already shown (SPEC.md §9.1).
 *
 * When a period closes, Home shows the **tide report** on the next open, and
 * the **swell** — "<Month> ended above water." — on the open after that, only
 * if it did. Each is shown once per closed period. What has been seen is a
 * per-device UI fact, like the theme, so it lives in `localStorage` and not in
 * Dexie: it is not a ledger row, it must never sync, and another device showing
 * its own report is correct rather than a duplicate.
 *
 * Keyed by the closed period's start, so a cut-off-day change that moves the
 * boundary yields a new key and nothing is wrongly marked seen.
 */

export type PeriodEndMoment = "report" | "swell";

const STORAGE_KEY = "aegae.periodEnd.v1";

type SeenRecord = Partial<Record<PeriodEndMoment, string[]>>;

/** Accept the original single-period marker when upgrading this device. */
function seenKeys(value: unknown): string[] {
  if (typeof value === "string") return [value];
  return Array.isArray(value) ? value.filter((key): key is string => typeof key === "string") : [];
}

function read(): SeenRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    const record = parsed as Record<string, unknown>;
    return {
      report: seenKeys(record.report),
      swell: seenKeys(record.swell),
    };
  } catch (cause) {
    // Storage disabled (a private window) or a value that is not JSON. Reported
    // to the console, and treated as "already seen" by `hasSeen`: a moment the
    // device cannot remember would otherwise repeat on every single open, which
    // is worse than not showing a once-a-month flourish at all.
    console.warn("Period-end state is unreadable on this device", cause);
    return null;
  }
}

/** Whether `moment` has been shown for the period starting at `periodKey`. */
export function hasSeen(moment: PeriodEndMoment, periodKey: string): boolean {
  const record = read();
  if (record === null) return true;
  return record[moment]?.includes(periodKey) ?? false;
}

/** Record that `moment` has been shown for the period starting at `periodKey`. */
export function markSeen(moment: PeriodEndMoment, periodKey: string): void {
  const record = read() ?? {};
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...record, [moment]: [...new Set([...(record[moment] ?? []), periodKey])] }));
  } catch (cause) {
    // Storage full or disabled. `hasSeen` already reads an unreadable store as
    // seen, so nothing repeats; the warning is the whole of the report.
    console.warn("Could not record a period-end moment on this device", cause);
  }
}
